/* eslint-disable */
import type { ProcessorStats } from '../core/types'

/**
 * Lightweight performance-stats collector for the video-processor pipeline.
 *
 * Tracks FPS (rolling 1-second window), dropped frames, total frames
 * processed, average per-frame latency, and uptime since {@link start}
 * was called.
 *
 * The class emits a `performance:warning` callback whenever the measured
 * FPS drops below {@link WARNING_FPS_THRESHOLD} for a sustained window,
 * allowing the consumer (typically {@link VideoProcessor}) to relay the
 * warning through the EventBus.
 */

const WARNING_FPS_THRESHOLD = 20
const FPS_WINDOW_MS = 1000

export interface StatsWarningCallback {
  (fps: number, droppedFrames: number): void
}

export class StatsCollector {
  private startTime = 0
  private running = false

  private frameTimestamps: number[] = []
  private totalFrames = 0
  private droppedFrames = 0

  private frameDurations: number[] = []
  private maxDurationSamples = 120

  private warningCallback: StatsWarningCallback | null = null
  private warningCheckTimer: number | null = null

  /**
   * Start collecting metrics. Resets all counters.
   *
   * @param onWarning - Optional callback invoked when measured FPS drops
   *                    below the warning threshold (~20 fps).
   */
  start (onWarning?: StatsWarningCallback): void {
    this.reset()
    this.startTime = performance.now()
    this.running = true
    this.warningCallback = onWarning ?? null

    if (this.warningCallback) {
      this.warningCheckTimer = window.setInterval(() => {
        this.checkPerformance()
      }, 2000)
    }
  }

  /** Stop collecting and clear the warning timer. */
  stop (): void {
    this.running = false
    if (this.warningCheckTimer !== null) {
      window.clearInterval(this.warningCheckTimer)
      this.warningCheckTimer = null
    }
  }

  /** Reset all counters without stopping the collector. */
  reset (): void {
    this.frameTimestamps = []
    this.totalFrames = 0
    this.droppedFrames = 0
    this.frameDurations = []
    this.startTime = performance.now()
  }

  /**
   * Record that one frame was processed successfully.
   *
   * @param processingMs - How many milliseconds the frame transform took.
   */
  recordFrame (processingMs?: number): void {
    if (!this.running) return

    const now = performance.now()
    this.frameTimestamps.push(now)
    this.totalFrames++

    if (typeof processingMs === 'number') {
      this.frameDurations.push(processingMs)
      if (this.frameDurations.length > this.maxDurationSamples) {
        this.frameDurations.shift()
      }
    }

    this.pruneOldTimestamps(now)
  }

  /** Record that one frame was dropped (e.g. processing took too long). */
  recordDrop (): void {
    if (!this.running) return
    this.droppedFrames++
  }

  /**
   * Returns a snapshot of all collected metrics.
   *
   * @param nsfwAnalysisCount - Externally-tracked NSFW analysis count
   *                            (from {@link NSFWDetector}).
   * @param avgNsfwLatencyMs  - Externally-tracked average NSFW latency
   *                            (from {@link NSFWDetector}).
   * @param activeFiltersCount - Number of currently active filters.
   */
  getSnapshot (
    nsfwAnalysisCount = 0,
    avgNsfwLatencyMs = 0,
    activeFiltersCount = 0
  ): ProcessorStats {
    return {
      fps: this.getCurrentFPS(),
      droppedFrames: this.droppedFrames,
      totalFramesProcessed: this.totalFrames,
      nsfwAnalysisCount,
      avgNsfwLatencyMs,
      avgFrameProcessingMs: this.getAvgFrameProcessingMs(),
      uptime: this.getUptimeMs(),
      activeFiltersCount
    }
  }

  /** Current frames-per-second based on a rolling 1-second window. */
  getCurrentFPS (): number {
    const now = performance.now()
    this.pruneOldTimestamps(now)
    return this.frameTimestamps.length
  }

  /** Milliseconds elapsed since {@link start} was called. */
  getUptimeMs (): number {
    if (!this.startTime) return 0
    return Math.round(performance.now() - this.startTime)
  }

  isRunning (): boolean {
    return this.running
  }

  /** Clean up timers and references. */
  destroy (): void {
    this.stop()
    this.warningCallback = null
  }

  private getAvgFrameProcessingMs (): number {
    if (this.frameDurations.length === 0) return 0
    const sum = this.frameDurations.reduce((a, b) => a + b, 0)
    return Math.round((sum / this.frameDurations.length) * 100) / 100
  }

  private pruneOldTimestamps (now: number): void {
    const cutoff = now - FPS_WINDOW_MS
    while (this.frameTimestamps.length > 0 && this.frameTimestamps[0] < cutoff) {
      this.frameTimestamps.shift()
    }
  }

  private checkPerformance (): void {
    if (!this.running || !this.warningCallback) return
    if (this.totalFrames < 30) return

    const fps = this.getCurrentFPS()
    if (fps > 0 && fps < WARNING_FPS_THRESHOLD) {
      this.warningCallback(fps, this.droppedFrames)
    }
  }
}
